import Phaser from 'phaser';

export default class GameOverScene extends Phaser.Scene {
  private finalDepth: number = 0;
  private autoTransitionTimer: Phaser.Time.TimerEvent | null = null;
  private failVoices: Phaser.Sound.BaseSound[] = [];

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: { depth: number }): void {
    // Store the final depth from the game scene
    this.finalDepth = data.depth || 0;
  }

  preload(): void {
    console.log('GameOverScene preload started');
    
    // Load failure voice samples - using MP3 files
    try {
      this.load.audio('fail-no', '/assets/no.mp3');
      this.load.audio('fail-ahh', '/assets/ahh.mp3');
      this.load.audio('fail-shoot', '/assets/shoot.mp3');
    } catch (e) {
      console.error('Error in preload:', e);
    }
  }

  create(): void {
    console.log('GameOverScene create started');
    
    // Set up failure voice samples
    this.setupFailureVoices();
    
    // Launch the game scene in the background but don't make it active
    // This will show the game scene underneath the game over overlay
    this.scene.launch('GameScene');
    this.scene.sendToBack('GameScene');
    this.scene.pause('GameScene');
    
    // Create a semi-transparent overlay for better text visibility
    const overlay = this.add.rectangle(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      this.cameras.main.width,
      this.cameras.main.height,
      0x00223f,
      0.3
    );
    overlay.setOrigin(0.5);
    
    // Create game over text
    const gameOverText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2 - 50,
      'GAME OVER',
      {
        fontSize: '48px',
        fontFamily: 'Arial',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4
      }
    );
    gameOverText.setOrigin(0.5);
    
    // Show final depth
    const finalScoreText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2 + 20,
      `${this.finalDepth}m`,
      {
        fontSize: '32px',
        fontFamily: 'Arial',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3
      }
    );
    finalScoreText.setOrigin(0.5);
    
    // Add a restart prompt
    const restartText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2 + 80,
      'Tap to restart',
      {
        fontSize: '18px',
        fontFamily: 'Arial',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2
      }
    );
    restartText.setOrigin(0.5);
    
    // Make the restart text pulse to draw attention
    this.tweens.add({
      targets: restartText,
      alpha: 0.5,
      duration: 800,
      yoyo: true,
      repeat: -1
    });
    
    // Play a random failure voice after a short delay
    this.time.delayedCall(300, () => {
      if (this.failVoices.length > 0) {
        try {
          const randomFailVoice = Phaser.Math.RND.pick(this.failVoices);
          randomFailVoice.play();
        } catch (e) {
          console.error('Error playing fail voice:', e);
        }
      }
    });
    
    // Add input handler to restart the game
    this.input.on('pointerdown', () => {
      if (this.autoTransitionTimer) {
        this.autoTransitionTimer.remove();
      }
      this.transitionToStartScene();
    });
    
    // Add a delay before allowing input to prevent accidental clicks
    this.input.enabled = false;
    this.time.delayedCall(1000, () => {
      this.input.enabled = true;
    });
    
    // Set up automatic transition after 1.5 seconds
    this.autoTransitionTimer = this.time.delayedCall(1500, () => {
      this.transitionToStartScene();
    });
    
    // Add a countdown timer
    const countdownDuration = 1; // seconds
    let timeRemaining = countdownDuration;
    
    const countdownText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2 + 120,
      `Restarting in ${timeRemaining}...`,
      {
        fontSize: '14px',
        fontFamily: 'Arial',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 1
      }
    );
    countdownText.setOrigin(0.5);
    
    // Update the countdown every second
    this.time.addEvent({
      delay: 1000,
      callback: () => {
        timeRemaining--;
        if (timeRemaining > 0) {
          countdownText.setText(`Restarting in ${timeRemaining}...`);
        } else {
          countdownText.setText('Restarting...');
        }
      },
      repeat: countdownDuration - 1
    });
    
    // Add some visual effects to make the background more interesting
    this.createBackgroundEffects();
  }
  
  private setupFailureVoices(): void {
    try {
      // Set up failure voice samples
      this.failVoices = [];
      
      // Only add sounds that successfully loaded
      if (this.sound.get('fail-no')) {
        this.failVoices.push(this.sound.add('fail-no', { volume: 0.7 }));
      }
      
      if (this.sound.get('fail-ahh')) {
        this.failVoices.push(this.sound.add('fail-ahh', { volume: 0.7 }));
      }
      
      if (this.sound.get('fail-shoot')) {
        this.failVoices.push(this.sound.add('fail-shoot', { volume: 0.7 }));
      }
      
      console.log(`Setup ${this.failVoices.length} failure voices`);
    } catch (e) {
      console.error('Error setting up failure voices:', e);
    }
  }
  
  private transitionToStartScene(): void {
    try {
      this.cameras.main.fade(500, 0, 0, 0);
      this.time.delayedCall(500, () => {
        this.scene.stop('GameScene'); // Stop the background scene
        this.scene.start('StartScene', { score: this.finalDepth });
      });
    } catch (e) {
      console.error('Error transitioning to start scene:', e);
      // Fallback direct transition if the fade transition fails
      this.scene.stop('GameScene');
      this.scene.start('StartScene', { score: this.finalDepth });
    }
  }
  
  private createBackgroundEffects(): void {
    // Add some floating bubbles in the background
    for (let i = 0; i < 15; i++) {
      const x = Phaser.Math.Between(0, this.cameras.main.width);
      const y = Phaser.Math.Between(0, this.cameras.main.height);
      const size = Phaser.Math.Between(5, 20);
      
      const bubble = this.add.circle(x, y, size, 0xffffff, 0.2);
      
      // Make bubbles float upward
      this.tweens.add({
        targets: bubble,
        y: y - Phaser.Math.Between(100, 300),
        x: x + Phaser.Math.Between(-50, 50),
        alpha: 0,
        duration: Phaser.Math.Between(3000, 6000),
        ease: 'Sine.easeInOut'
      });
    }
  }
} 